import { toolCallStore, type ChatCardStage } from "@/shared/ui/chat-card/tool-call-store";
import type { BatchScope } from "@/features/order-instructions/lib/order-batch";

export interface OrderBatchSeed {
  clientId: string | null;
  clientQuery: string | null;
}

export interface OrderBatch {
  toolCallId: string;
  seed: OrderBatchSeed;
  scope: BatchScope | null;
  opened: string[];
  settled: boolean;
}

export function openOrderBatch(toolCallId: string, seed: OrderBatchSeed): void {
  const opened = [...new Set(STORE.all().flatMap((earlier) => earlier.opened))];
  STORE.open({ toolCallId, seed, scope: null, opened, settled: false });
}

export function orderBatchAnswered(toolCallId: string): void {
  STORE.answered(toolCallId);
}

export function orderBatchAnswering(toolCallId: string): boolean {
  return STORE.answering(toolCallId);
}

export function hasOrderBatch(toolCallId: string): boolean {
  return STORE.has(toolCallId);
}

export function useOrderBatch(toolCallId: string): OrderBatch | null {
  return STORE.useEntry(toolCallId);
}

export function useOrderBatchAnswering(toolCallId: string): boolean {
  return STORE.useAnswering(toolCallId);
}

export function useOrderBatchStages(): (toolCallId: string) => ChatCardStage | null {
  return STORE.useStages();
}

export function chooseOrderBatchScope(toolCallId: string, scope: BatchScope): void {
  STORE.update(toolCallId, (entry) => ({ ...entry, scope }));
}

export function markOrderBatchOpened(toolCallId: string, recordId: string): void {
  STORE.update(toolCallId, (entry) =>
    entry.opened.includes(recordId) ? entry : { ...entry, opened: [...entry.opened, recordId] },
  );
}

export function markOrderBatchSettled(toolCallId: string, settled: boolean): void {
  if (STORE.read(toolCallId)?.settled === settled) return;
  STORE.update(toolCallId, (entry) => ({ ...entry, settled }));
}

function narrowEntry(value: unknown): OrderBatch | null {
  if (!isRecord(value) || typeof value.toolCallId !== "string") return null;
  const seed = isRecord(value.seed) ? value.seed : {};
  return {
    toolCallId: value.toolCallId,
    seed: { clientId: text(seed.clientId), clientQuery: text(seed.clientQuery) },
    scope: scope(value.scope),
    opened: Array.isArray(value.opened)
      ? value.opened.filter((id): id is string => typeof id === "string" && id !== "")
      : [],
    settled: value.settled === true,
  };
}

function scope(value: unknown): BatchScope | null {
  if (!isRecord(value)) return null;
  if (value.by === "book") return { by: "book" };
  const { kind, subjectId, clientLinkId, label } = value;
  if (value.by !== "client" || (kind !== "individual" && kind !== "corporation")) return null;
  if (typeof subjectId !== "string" || subjectId === "") return null;
  if (typeof clientLinkId !== "string" || clientLinkId === "") return null;
  if (typeof label !== "string") return null;
  return { by: "client", kind, subjectId, clientLinkId, label };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function text(value: unknown): string | null {
  return typeof value === "string" && value !== "" ? value : null;
}

const STORE = toolCallStore<OrderBatch>({
  key: "simon.orders.chatBatch",
  narrow: narrowEntry,
  stage: (entry) => (entry.settled ? "done" : "awaiting"),
});
